import { useCallback,useEffect,useRef,useState,type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { X } from 'lucide-react';
import { hasPreviousLogin,rememberPreviousLogin } from '../lib/returningUser';
import { clearAllDocumentAnalysisTransfers } from '../services/documentTransfer';
import { AuthContext,type User } from './AuthContext.shared';

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isReturningUser, setIsReturningUser] = useState(hasPreviousLogin);
  const authenticatedUserId = useRef<string | null>(null);
  const navigate = useNavigate();
  const generation = useRef(0);
  const [sessionNotice, setSessionNotice] = useState(() =>
    new URLSearchParams(window.location.search).get('session_replaced') === '1'
      ? 'Ai fost conectat aici. Sesiunea anterioară de pe celălalt dispozitiv sau browser a fost deconectată. Contul permite o singură sesiune activă.'
      : '');

  const refreshUser = useCallback(async () => {
    const currentGeneration = generation.current;
    try {
      const response = await fetch('/api/auth/me', { cache: 'no-store', credentials: 'include' });
      const contentType = response.headers.get('content-type') || '';
      const data = contentType.includes('application/json') ? await response.json() : null;
      if (currentGeneration !== generation.current) return;
      if (response.ok && contentType.includes('application/json')) {
        const userData = data;
        if (userData) {
          authenticatedUserId.current = userData.id;
          rememberPreviousLogin();
          setIsReturningUser(true);
          setUser(userData);
          setIsAuthenticated(true);
        } else {
          const wasAuthenticated = authenticatedUserId.current !== null;
          authenticatedUserId.current = null;
          setUser(null);
          setIsAuthenticated(false);
          if (wasAuthenticated) navigate('/cautare', { replace: true });
        }
      } else if (response.status === 401) {
        const wasAuthenticated = authenticatedUserId.current !== null;
        authenticatedUserId.current = null;
        setUser(null);
        setIsAuthenticated(false);
        if (data?.detail === 'SESSION_REVOKED') {
          rememberPreviousLogin();
          setIsReturningUser(true);
          setSessionNotice('Pentru protecția contului tău, această sesiune a fost închisă în urma unei autentificări noi sau a modificării datelor de acces. Te invităm să te autentifici din nou pentru a continua în siguranță.');
        }
        if (wasAuthenticated) navigate('/cautare', { replace: true });
      }
    } catch {
      // A temporary network failure is not proof that the session was revoked.
    } finally {
      if (currentGeneration === generation.current) setIsLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    void refreshUser();
    const url = new URL(window.location.href);
    if (url.searchParams.has('session_replaced')) {
      url.searchParams.delete('session_replaced');
      window.history.replaceState(window.history.state, '', url);
    }
  }, [refreshUser]);

  useEffect(() => {
    if (!isAuthenticated) return;
    let pending = false;
    const check = async () => {
      if (pending || document.visibilityState === 'hidden') return;
      pending = true;
      try { await refreshUser(); } finally { pending = false; }
    };
    const timer = window.setInterval(check, 15000);
    window.addEventListener('focus', check);
    document.addEventListener('visibilitychange', check);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener('focus', check);
      document.removeEventListener('visibilitychange', check);
    };
  }, [isAuthenticated, refreshUser]);

  const login = (userData: User, nextPath?: string) => {
    generation.current += 1;
    authenticatedUserId.current = userData.id;
    rememberPreviousLogin();
    setIsReturningUser(true);
    setSessionNotice(userData.session_replaced
      ? 'Ai fost conectat aici. Sesiunea anterioară de pe celălalt dispozitiv sau browser a fost deconectată. Contul permite o singură sesiune activă.'
      : '');
    setUser(userData);
    setIsAuthenticated(true);
    if (userData.needs_profile_completion) {
      navigate('/complete-profile');
    } else {
      const requestedPath = nextPath || userData.post_auth_path;
      const safePath = requestedPath && (
        requestedPath === '/cautare' ||
        /^\/abonamente\?plan=(premium_monthly|premium_semiannual|premium_annual)$/.test(requestedPath)
      ) ? requestedPath : '/cautare';
      navigate(safePath);
    }
  };

  const logout = async () => {
    generation.current += 1;
    clearAllDocumentAnalysisTransfers();
    try {
      await fetch('/api/auth/logout', { method: 'POST' });
    } finally {
      generation.current += 1;
      authenticatedUserId.current = null;
      setUser(null);
      setIsAuthenticated(false);
      clearAllDocumentAnalysisTransfers();
      navigate('/login');
    }
  };

  return (
    <AuthContext.Provider value={{ user, isAuthenticated, isLoading, isReturningUser, login, logout, refreshUser }}>
      {sessionNotice && (
        <div
          role="status"
          className="fixed top-4 left-4 right-4 z-[200] mx-auto max-w-lg rounded-2xl border-2 border-indigo-500/30 bg-white dark:bg-slate-900 p-4 text-sm text-slate-800 dark:text-slate-100 shadow-2xl select-none animate-in fade-in slide-in-from-top-4"
        >
          <div className="flex items-start justify-between gap-3">
            <p className="flex-1 text-xs sm:text-sm leading-relaxed">{sessionNotice}</p>
            <button
              type="button"
              onClick={() => setSessionNotice('')}
              className="p-1 rounded-lg text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 transition-colors shrink-0"
              aria-label="Închide"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          <button
            type="button"
            onClick={() => setSessionNotice('')}
            className="mt-3 w-full sm:w-auto px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold transition-colors shadow-xs"
          >
            Am înțeles
          </button>
        </div>
      )}
      {children}
    </AuthContext.Provider>
  );
};
